import React, { useEffect, useRef, useState } from 'react';
import { select } from 'd3-selection';
import { geoGraticule, geoPath, geoAlbersUsa } from 'd3-geo';
import { SegmentSelect } from 'utils';
import { WppTypography } from '@wppopen/components-library-react';
import styles from './D3geoMap.module.scss';

const { D3geoMapBackground, D3geoMapTooltip, D3geoMapTooltipText } = styles;

interface ID3geoMapProps {
    selectedItems?: string[];
    handleChange: (seletedDmaId: number) => void;
    geojsonMap?: any;
    geojsonPath?: any;
    permissions?: any;
    filterBy: SegmentSelect;
    dataTestId: string;
}

const COLOR_DEFAULT = '#FFFFFF';
const COLOR_UNSELECTED = '#E7EAEE';
const COLOR_SELECTED = '#8508E8';
const SVG_BORDER_CCOLOR = '#D9DCDE';

export const D3geoMap = ({
    geojsonMap,
    geojsonPath,
    permissions,
    filterBy,
    selectedItems,
    dataTestId,
    handleChange,
}: ID3geoMapProps) => {
    const [currentSelection, setCurrentSelection] = useState<string>('');
    const dmaContainer = useRef<HTMLDivElement>(null);
    const shouldShowDmaTooltip = true;

    useEffect(() => {
        const width = 502;
        const height = 290;
        const projection = geoAlbersUsa()
            .scale(width)
            .translate([width / 1.8, height / 2.1]);
        const geoGenerator = geoPath().projection(projection);
        const svg = select('#dma-map').append('svg').attr('width', width).attr('height', height);
        const graticule = geoGraticule()
            .extent([
                [-98 - 45, 38 - 45],
                [-98 + 45, 38 + 45],
            ])
            .step([5, 5]);
        svg.append('rect')
            .datum(graticule)
            .attr('id', D3geoMapBackground)
            .attr('width', width)
            .attr('height', height);
        const g = svg.append('g');

        const onChange = (value: any) => {
            handleChange(value);
        };

        g.append('g')
            .attr('id', 'dmas')
            .selectAll('path')
            .data(geojsonMap.features)
            .enter()
            .append('path')
            .attr('d', geoGenerator as any)
            .attr('opacity', 1)
            .attr('fill', COLOR_DEFAULT)
            .attr('stroke', SVG_BORDER_CCOLOR);

        select('#dmas')
            .selectAll('path')
            .on('click', function (event: any) {
                const dmaId = event.target.__data__.properties.DMA_NAME;
                if (permissions.includes(dmaId)) {
                    onChange(dmaId);
                }
            })
            .on('mouseover', function (event: any) {
                const dmaId = event.target.__data__.properties.DMA_NAME;
                setCurrentSelection(dmaId);
            });

        select('#dmas')
            .selectAll('path')
            .data(
                geojsonMap.features.filter((e: any) => permissions.includes(e.properties.DMA_NAME)),
            )
            .attr('fill', (d: any) =>
                selectedItems?.some((id) => {
                    return id === d.properties.DMA_NAME;
                })
                    ? COLOR_SELECTED
                    : COLOR_UNSELECTED,
            )
            .attr('cursor', 'pointer');

        return () => {
            g.remove();
            svg.remove();
            setCurrentSelection('');
        };
    }, [
        dmaContainer,
        geojsonMap.features,
        geojsonMap,
        geojsonPath,
        permissions,
        filterBy,
        handleChange,
        selectedItems,
    ]);

    return (
        <div ref={dmaContainer}>
            <div id="dma-map" data-testid={dataTestId}></div>
            {shouldShowDmaTooltip && currentSelection && (
                <div id={D3geoMapTooltip}>
                    <WppTypography id={D3geoMapTooltipText} type="s-body">
                        {currentSelection}
                    </WppTypography>
                </div>
            )}
        </div>
    );
};
export default D3geoMap;
